import React, { ReactNode, Key } from 'react';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownProps,
  DropdownTriggerProps,
  DropdownMenuProps,
  Button,
} from '@heroui/react';
import { OverlayPlacement } from '@heroui/aria-utils';

export interface DropdownItemType {
  key: string;
  label: ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'default';
  startContent?: ReactNode;
  endContent?: ReactNode;
  className?: string;
  description?: string;
}

interface CustomDropdownProps {
  items: DropdownItemType[];
  triggerContent: ReactNode;
  placement?: OverlayPlacement;
  dropdownProps?: Partial<DropdownProps>;
  triggerProps?: Partial<DropdownTriggerProps>;
  menuProps?: Partial<DropdownMenuProps>;
  buttonProps?: React.ComponentProps<typeof Button>;
  onAction?: (key: Key) => void;
}

type ClassNameValue = string | false | undefined | null;
type ClassNameObject = Record<string, ClassNameValue>;

const mergeClassNames = (defaultClasses: ClassNameObject, customClasses?: ClassNameObject) => {
  const merged = { ...defaultClasses };
  if (customClasses) {
    Object.keys(customClasses).forEach((key) => {
      if (typeof customClasses[key] === 'string') {
        merged[key] = merged[key] ? `${merged[key]} ${customClasses[key]}`.trim() : customClasses[key];
      } else {
        merged[key] = customClasses[key];
      }
    });
  }
  return merged;
};

const TmdxDropdownMenu: React.FC<CustomDropdownProps> = ({
  items,
  triggerContent,
  placement = 'bottom',
  dropdownProps,
  triggerProps,
  menuProps,
  onAction,
}) => {
  const defaultDropdownClasses: ClassNameObject = {
    content: 'p-0 shadow-none',
  };

  const defaultMenuClasses: ClassNameObject = {
    base: 'bg-base-60 p-1 rounded-lg shadow-xl relative *:text-m-r!',
  };

  const mergedDropdownClasses = mergeClassNames(defaultDropdownClasses, dropdownProps?.classNames as ClassNameObject);
  const mergedMenuClasses = mergeClassNames(defaultMenuClasses, menuProps?.classNames as ClassNameObject);

  return (
    <Dropdown
      placement={placement}
      {...dropdownProps}
      classNames={{ ...dropdownProps?.classNames, ...mergedDropdownClasses }}
    >
      <DropdownTrigger {...triggerProps}>{triggerContent}</DropdownTrigger>
      <DropdownMenu
        aria-label="Dropdown menu"
        onAction={onAction}
        {...menuProps}
        classNames={{ ...menuProps?.classNames, ...mergedMenuClasses }}
      >
        {items.map((item) => (
          <DropdownItem
            key={item.key}
            color={item.color}
            startContent={item.startContent}
            endContent={item.endContent}
            className={`px-2 py-3 data-[hover=true]:bg-base-50 rounded-lg [&>svg]:w-4 [&>svg]:h-4 *:text-m-r! ${item.className || ''} ${item.color === 'primary' ? '*:text-base-5' : '*:text-danger-40'}`}
            description={item.description}
          >
            {item.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};

export default TmdxDropdownMenu;
